// frontend/src/components/Common/MentoringCallPage.js
//
// Shared 1:1 mentoring call page — used by both faculty and student routes
// (they differ only in how they resolve the tenant slug and which join/
// leave API calls they make). Plain 2-party Agora RTC call: no translation
// relay, no host/audience broadcast roles — both sides publish camera+mic
// and subscribe to the other's tracks symmetrically.
import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AgoraRTC from 'agora-rtc-sdk-ng';
import { Button, Spinner, Alert, Badge } from 'react-bootstrap';
import { joinMentoringSlotFaculty, joinMentoringSlotStudent } from '../../api/mentoringApi';
import '../../liveSessionBroadcast.css';

const NAVY = '#092957';
const GOLD = '#f1a51c';

const decodeToken = (token) => {
    try {
        return JSON.parse(atob(token.split('.')[1]));
    } catch {
        return null;
    }
};

const formatCountdown = (ms) => {
    if (ms <= 0) return '00:00';
    const totalSec = Math.floor(ms / 1000);
    const m = String(Math.floor(totalSec / 60)).padStart(2, '0');
    const s = String(totalSec % 60).padStart(2, '0');
    return `${m}:${s}`;
};

// role: 'faculty' | 'student'
const MentoringCallPage = ({ role, backTo }) => {
    const { slotId } = useParams();
    const navigate = useNavigate();
    const slug = role === 'faculty'
        ? decodeToken(localStorage.getItem('access_token'))?.tenant_slug
        : 'samkalp-ias';

    const clientRef = useRef(null);
    const micTrackRef = useRef(null);
    const camTrackRef = useRef(null);
    const remoteVideoElRef = useRef(null);
    const localVideoElRef = useRef(null);

    const [error, setError] = useState('');
    const [joined, setJoined] = useState(false);
    const [joining, setJoining] = useState(true);
    const [endUtc, setEndUtc] = useState(null);
    const [countdown, setCountdown] = useState(0);
    const [micMuted, setMicMuted] = useState(false);
    const [camOn, setCamOn] = useState(true);

    useEffect(() => {
        if (!endUtc) return undefined;
        const tick = () => setCountdown(new Date(endUtc).getTime() - Date.now());
        tick();
        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
    }, [endUtc]);

    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            setJoining(true);
            setError('');
            try {
                const joinFn = role === 'faculty' ? joinMentoringSlotFaculty : joinMentoringSlotStudent;
                const { agora, end_utc } = await joinFn(slug, slotId);
                if (cancelled) return;
                setEndUtc(end_utc);

                const client = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
                await client.join(agora.appId, agora.channel, agora.token, agora.uid || null);

                const micTrack = await AgoraRTC.createMicrophoneAudioTrack();
                const camTrack = await AgoraRTC.createCameraVideoTrack();
                await client.publish([micTrack, camTrack]);
                camTrack.play(localVideoElRef.current);

                client.on('user-published', async (user, mediaType) => {
                    await client.subscribe(user, mediaType);
                    if (mediaType === 'video' && remoteVideoElRef.current) user.videoTrack?.play(remoteVideoElRef.current);
                    if (mediaType === 'audio') user.audioTrack?.play();
                });

                clientRef.current = client;
                micTrackRef.current = micTrack;
                camTrackRef.current = camTrack;
                setJoined(true);
            } catch (err) {
                if (!cancelled) setError(err?.message || 'Could not join the mentoring call. Check camera/mic permissions and try again.');
            } finally {
                if (!cancelled) setJoining(false);
            }
        };
        run();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug, slotId, role]);

    useEffect(() => () => {
        micTrackRef.current?.close();
        camTrackRef.current?.close();
        clientRef.current?.leave();
    }, []);

    const toggleMic = async () => {
        if (!micTrackRef.current) return;
        const next = !micMuted;
        await micTrackRef.current.setEnabled(!next);
        setMicMuted(next);
    };

    const toggleCamera = async () => {
        if (!camTrackRef.current) return;
        const next = !camOn;
        await camTrackRef.current.setEnabled(next);
        setCamOn(next);
    };

    const leaveCall = async () => {
        micTrackRef.current?.close();
        camTrackRef.current?.close();
        await clientRef.current?.leave();
        navigate(backTo);
    };

    return (
        <div style={{ minHeight: '100vh', background: NAVY, color: '#fff', display: 'flex', flexDirection: 'column' }}>
            <header className="live-header" style={{ padding: '16px 24px', borderBottom: '1px solid rgba(255,255,255,0.15)' }}>
                <div>
                    <Badge bg="danger" className="me-2">1:1 MENTORING</Badge>
                    {endUtc && <span className="small" style={{ color: GOLD }}>Time remaining: {formatCountdown(countdown)}</span>}
                </div>
            </header>

            {error && <Alert variant="danger" className="m-3 py-2 small">{error}</Alert>}
            {joining && !error && (
                <div className="d-flex justify-content-center align-items-center flex-grow-1">
                    <Spinner animation="border" variant="light" />
                </div>
            )}

            {joined && (
                <div style={{ flex: 1, position: 'relative', padding: 24 }}>
                    <div ref={remoteVideoElRef} style={{ width: '100%', height: '70vh', background: '#000', borderRadius: 12 }} />
                    <div ref={localVideoElRef} style={{ position: 'absolute', bottom: 40, right: 40, width: 180, height: 135, background: '#000', borderRadius: 8, border: '2px solid rgba(255,255,255,0.3)' }} />

                    <div className="d-flex gap-2 justify-content-center mt-3">
                        <Button variant={micMuted ? 'warning' : 'outline-light'} onClick={toggleMic}>{micMuted ? '🔇 Unmute Mic' : '🎙️ Mute Mic'}</Button>
                        <Button variant={camOn ? 'outline-light' : 'light'} onClick={toggleCamera}>{camOn ? 'Stop Camera' : 'Start Camera'}</Button>
                        <Button variant="danger" onClick={leaveCall}>Leave Call</Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MentoringCallPage;
